import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Form,
  Grid,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Popover,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Transfer,
  Upload,
} from "antd";
import styled from "styled-components";
import {
  AppstoreOutlined,
  FileAddOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useFirebase, useFirestore } from "react-redux-firebase";
import {
  FIRESTORE_PRODUCT_DRAFTS_TABLE,
  FIRESTORE_PURCHASE_ORDERS_TABLE,
} from "../../../../constants/FirestoreConstant";
import {
  PRODUCT_DRAFT_STATUS_NEEDS_SUPPLIER_INPUT,
  PURCHASE_ORDER_STATUS_CANCELLED,
  PURCHASE_ORDER_STATUS_COMPLETED,
  PURCHASE_ORDER_STATUS_DEPOSIT_PAID,
  PURCHASE_ORDER_STATUS_DRAFT,
  PURCHASE_ORDER_STATUS_IN_PRODUCTION,
  PURCHASE_ORDER_STATUS_PAID,
  PURCHASE_ORDER_STATUS_PAYMENT_PENDING,
  PURCHASE_ORDER_STATUS_PENDING_APPROVAL,
  PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION,
  PURCHASE_ORDER_STATUS_PENDING_SUPPLIER_REVIEW,
  PURCHASE_ORDER_STATUS_READY_FOR_PRODUCTION,
  PURCHASE_ORDER_STATUS_RECEIVED,
  PURCHASE_ORDER_STATUS_SHIPPED,
  PURCHASE_ORDER_STATUS_SPLIT,
  PURCHASE_ORDER_STATUS_UNDER_REVIEW,
} from "../../../../constants/OrderConstant";
import { useTranslation } from "react-i18next";
import { addLogEntry } from "../../../../utils/logging";
import { useSuppliers } from "../../../../data/hooks/useSuppliers";
import { usePurchaseOrderShipments } from "../../../../data/hooks";
import { useSalesAggregates } from "../../../../data/hooks/useSalesAggregates";
import { compressImage } from "../../../../functions/images";
import urls from "../../../../urls";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";
import {
  formatPriceByCurrency,
  formatPriceUsd,
  getFormattedProductColor,
  getPurchaseOrderStatusColor,
} from "../../../../functions/formatters";
import {
  getProductInNonCompletedOrders,
  getShippedQuantityForOrder,
  getProductQuantityInOrders,
  getShippedQuantityInOrders,
} from "../../../../utils/stockCalculations";

const PurchaseOrdersOverview = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [addForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const purchaseOrders = useSelector((state) => state.firestoreReducer.ordered.purchaseOrders);
  const { suppliers, loading: suppliersLoading } = useSuppliers();
  const { shipments, loading: shipmentsLoading } = usePurchaseOrderShipments();
  const config = useSelector((state) => state.firestoreReducer.ordered.config);

  // State declarations must come before useEffect hooks that use them
  const [searchText, setSearchText] = useState(searchParams.get("q") || "");
  const [displayCount, setDisplayCount] = useState(20);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "active");
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get("supplier") || null);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);

  // Load filters from localStorage on component mount
  React.useEffect(() => {
    try {
      const savedFilters = localStorage.getItem("purchaseOrdersFilters");
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.searchText) setSearchText(filters.searchText);
        if (filters.statusFilter) setStatusFilter(filters.statusFilter);
        if (filters.supplierFilter) setSupplierFilter(filters.supplierFilter);
        if (filters.currentPage) setCurrentPage(filters.currentPage);
      } else {
        // Default: hide cancelled and completed orders
        setStatusFilter("active");
      }
    } catch (error) {
      console.error("Error initializing filters:", error);
      // Fallback to default values if localStorage fails
      setStatusFilter("active");
      setSearchText("");
      setSupplierFilter(null);
      setCurrentPage(1);
    }
  }, []);

  // Save filters to localStorage whenever they change
  React.useEffect(() => {
    try {
      const filters = {
        searchText,
        statusFilter,
        supplierFilter,
        currentPage,
      };
      localStorage.setItem("purchaseOrdersFilters", JSON.stringify(filters));
    } catch (error) {
      console.warn("Failed to save filters to localStorage:", error);
      // Silently fail - filters will work but won't persist
    }
  }, [searchText, statusFilter, supplierFilter, currentPage]);

  const updateSearchParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "All") params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };
  const [tempOrderData, setTempOrderData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [productAttachments, setProductAttachments] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSelectionStep, setProductSelectionStep] = useState(1); // 1 = select products, 2 = enter details
  const [salesPeriod, setSalesPeriod] = useState(null); // [startDate, endDate]
  const [productSalesData, setProductSalesData] = useState({});
  const [productSearchText, setProductSearchText] = useState("");
  const [productViewMode, setProductViewMode] = useState("table"); // "table" or "transfer"
  const [productTableKey, setProductTableKey] = useState(0);
  const [hasDraftRecovery, setHasDraftRecovery] = useState(false);
  const [draftProducts, setDraftProducts] = useState([]); // Draft products (not yet in catalog)
  const [addDraftProductModalOpen, setAddDraftProductModalOpen] = useState(false);
  const [draftProductForm] = Form.useForm();
  const { t } = useTranslation();

  const firestore = useFirestore();
  const firebase = useFirebase();
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const categories = useSelector((state) => state.firestoreReducer.ordered.categories);
  const profile = useSelector((state) => state.firebaseReducer.profile);
  const { email } = profile;

  // Use sales aggregates hook
  const { getProductSalesInPeriod } = useSalesAggregates({ autoFetch: false });

  // Save draft to Firestore
  const handleSaveDraftToDatabase = async () => {
    try {
      setIsSubmitting(true);
      const values = addForm.getFieldsValue();

      // Build order products from form values or selectedProducts state
      // If form doesn't have products yet (still in step 1), use selectedProducts
      let productSources = values.products || [];
      if (productSources.length === 0 && selectedProducts.length > 0) {
        productSources = selectedProducts.map((productId) => ({ product: productId }));
      }

      // Separate regular products from draft products in form
      const regularProductSources = productSources.filter((p) => p?.product && !p?.isDraft);
      const draftProductSources = productSources.filter((p) => p?.isDraft);

      const orderProducts = regularProductSources.map((product) => {
        const productData = products.find((p) => p.id === product.product);

        let productDimensions = null;
        if (productData) {
          if (productData.length && productData.width && productData.height) {
            productDimensions = {
              length: productData.length,
              width: productData.width,
              height: productData.height,
            };
          } else if (productData.height && productData.diameter) {
            productDimensions = {
              height: productData.height,
              diameter: productData.diameter,
            };
          } else if (productData.length && productData.width) {
            productDimensions = {
              length: productData.length,
              width: productData.width,
            };
          }
        }

        const baseProduct = {
          productId: product.product,
          amount: product.amount || 0,
          productName: productData?.name || null,
          productColor: productData?.color || null,
          productCode: productData?.code || null,
          productBarcode: productData?.barcode || null,
          productImages: productData?.image || [],
          productDimensions: productDimensions,
          innerPackagingQuantity: product.innerPackagingQuantity || null,
          masterPackagingQuantity: product.masterPackagingQuantity || null,
          price: product.price || 0,
          priority: product.priority || false,
          supplierMetadata: productData?.supplierMetadata || null,
        };

        // Add attachments if they exist
        const attachments = productAttachments[product.product];
        if (attachments?.hasAttachments) {
          if (attachments.comment) {
            baseProduct.comment = attachments.comment;
          }
          // Only save existing image URLs (new files will be uploaded when finalizing)
          if (attachments.fileList && attachments.fileList.length > 0) {
            const existingUrls = attachments.fileList.filter((f) => f.url).map((f) => f.url);
            if (existingUrls.length > 0) {
              baseProduct.images = existingUrls;
            }
          }
        }

        return baseProduct;
      });

      // Build draft products from form (step 2) or from draftProducts state (step 1)
      const draftOrderProducts =
        draftProductSources.length > 0
          ? draftProductSources.map((draft) => ({
              productId: null,
              productDraftId: draft.draftTempId || `draft-${Date.now()}-${Math.random()}`,
              amount: draft.amount || 0,
              productName: draft.draftProductName,
              productCode: draft.draftProductCode,
              productBarcode: draft.draftProductBarcode || null,
              productImages: [],
              productDimensions: null,
              innerPackagingQuantity: null,
              masterPackagingQuantity: null,
              price: draft.price || 0,
              priority: draft.priority || false,
              supplierMetadata: null,
              isDraft: true,
            }))
          : draftProducts.map((draft) => ({
              productId: null,
              productDraftId: draft.tempId,
              amount: 0,
              productName: draft.productName,
              productCode: draft.productCode,
              productBarcode: draft.productBarcode,
              productImages: [],
              productDimensions: null,
              innerPackagingQuantity: null,
              masterPackagingQuantity: null,
              price: 0,
              priority: false,
              supplierMetadata: null,
              isDraft: true,
            }));

      const allOrderProducts = [...orderProducts, ...draftOrderProducts];

      const totalProducts = allOrderProducts.reduce((sum, product) => sum + (product.amount || 0), 0);
      const totalPrice = allOrderProducts.reduce(
        (sum, product) => sum + (product.price || 0) * (product.amount || 0),
        0,
      );

      if (editingOrder) {
        // Update existing order (already a draft or being edited)
        const selectedSupplier = suppliers.find((s) => s.id === values.supplier);

        // Create ProductDraft documents for new draft products
        const productsWithDraftIds = await createProductDraftsForOrder(
          allOrderProducts,
          editingOrder.id,
          values.supplier,
          email,
        );

        const updateData = {
          supplierId: values.supplier || null,
          supplierName: selectedSupplier?.name || null,
          currency: selectedSupplier?.currency || "USD",
          totalProducts,
          totalPrice,
          exchangeRate: values.exchangeRate || null,
          products: productsWithDraftIds,
          comments: values.comments || "",
          updatedAt: dayjs().unix(),
          updatedBy: email,
          lastModifiedBy: { email, updatedAt: dayjs().unix() },
        };

        // Handle order-level images
        const existingOrderUrls = fileList.filter((f) => f.url).map((f) => f.url);
        updateData.images = existingOrderUrls;

        const oldLog = editingOrder.log || [];
        const newLog = addLogEntry(oldLog, {
          action: "draft_saved",
          user: email,
        });
        updateData.log = newLog;

        await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(editingOrder.id).update(updateData);

        // Sync product prices and packaging to products collection
        await updateProductPricesAndPackaging(orderProducts);

        messageApi.success(t("draft_saved_successfully"));
      } else {
        // Create new draft order - first get and increment counter
        const configDocRef = firestore.collection("config").doc("orders");
        let newOrderNumber;

        await firestore.runTransaction(async (transaction) => {
          const configSnapshot = await transaction.get(configDocRef);
          const currentCounter = configSnapshot.data()?.currentOrderCounter || 0;
          const newCounter = currentCounter + 1;
          transaction.update(configDocRef, { currentOrderCounter: newCounter });
          newOrderNumber = `IO-${dayjs().format("YYYYMMDD")}-${newCounter}`;
        });

        const selectedSupplierForNew = suppliers.find((s) => s.id === values.supplier);
        const newOrder = {
          orderNumber: newOrderNumber,
          supplierId: values.supplier || null,
          supplierName: selectedSupplierForNew?.name || null,
          currency: selectedSupplierForNew?.currency || "USD",
          totalProducts,
          totalPrice,
          exchangeRate: values.exchangeRate || null,
          status: PURCHASE_ORDER_STATUS_DRAFT,
          orderDate: dayjs().unix(),
          products: allOrderProducts, // Will be updated with real draft IDs after PO is created
          comments: values.comments || "",
          images: [],
          createdBy: email,
          createdAt: dayjs().unix(),
          log: [
            {
              action: "draft_created",
              timestamp: dayjs().unix(),
              user: email,
            },
          ],
        };

        // Add the draft order outside the transaction so listener picks it up immediately
        const docRef = await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).add(newOrder);

        // Now create ProductDraft documents and update the PO with real draft IDs
        if (draftProducts.length > 0) {
          const productsWithDraftIds = await createProductDraftsForOrder(
            allOrderProducts,
            docRef.id,
            values.supplier,
            email,
          );
          await firestore
            .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
            .doc(docRef.id)
            .update({
              products: productsWithDraftIds,
              lastModifiedBy: { email, updatedAt: dayjs().unix() },
            });
        }

        setEditingOrder({ ...newOrder, id: docRef.id });

        // Sync product prices and packaging to products collection
        await updateProductPricesAndPackaging(orderProducts);

        messageApi.success(t("draft_saved_successfully"));
      }

      closeAddModal();
    } catch (error) {
      console.error("Error saving draft:", error);
      messageApi.error(t("draft_save_failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load draft order into form
  const loadDraftOrder = (draftOrder) => {
    setEditingOrder(draftOrder);

    // Separate regular products from draft products
    const regularProducts = (draftOrder.products || []).filter((p) => p.productId);
    const draftProductsFromOrder = (draftOrder.products || []).filter((p) => !p.productId && p.productDraftId);

    // Pre-fill form with order data - include both regular and draft products
    const formProducts = [
      // Regular products
      ...regularProducts.map((product) => ({
        product: product.productId,
        amount: product.amount,
        innerPackagingQuantity: product.innerPackagingQuantity,
        masterPackagingQuantity: product.masterPackagingQuantity,
        price: product.price,
        priority: product.priority || false,
      })),
      // Draft products
      ...draftProductsFromOrder.map((product) => ({
        product: null,
        isDraft: true,
        draftTempId: product.productDraftId,
        draftProductName: product.productName,
        draftProductCode: product.productCode,
        draftProductBarcode: product.productBarcode,
        amount: product.amount || 0,
        price: product.price || 0,
        priority: product.priority || false,
      })),
    ];

    addForm.setFieldsValue({
      supplier: draftOrder.supplierId,
      exchangeRate: draftOrder.exchangeRate,
      products: formProducts,
      comments: draftOrder.comments || "",
    });

    // Set selected products from the order (only regular products)
    const productIds = regularProducts.map((product) => product.productId).filter(Boolean);
    setSelectedProducts(productIds);

    // Set draft products state for display in step 1
    setDraftProducts(
      draftProductsFromOrder.map((p) => ({
        tempId: p.productDraftId,
        productName: p.productName,
        productCode: p.productCode,
        productBarcode: p.productBarcode,
        isDraft: true,
      })),
    );

    // Set step based on whether any products are selected (regular or draft)
    const hasProducts = productIds.length > 0 || draftProductsFromOrder.length > 0;
    setProductSelectionStep(hasProducts ? 2 : 1);

    // Set existing images if any
    if (draftOrder.images && draftOrder.images.length > 0) {
      const existingFiles = draftOrder.images.map((url, index) => ({
        uid: `existing-${index}`,
        name: `image-${index}`,
        status: "done",
        url: url,
      }));
      setFileList(existingFiles);
    }

    // Set product attachments if any
    const attachments = {};
    (draftOrder.products || []).forEach((product) => {
      if (product.comment || (product.images && product.images.length > 0)) {
        attachments[product.productId] = {
          hasAttachments: true,
          comment: product.comment || "",
          fileList: product.images
            ? product.images.map((url, index) => ({
                uid: `existing-${product.productId}-${index}`,
                name: `image-${index}`,
                status: "done",
                url: url,
              }))
            : [],
        };
      }
    });
    setProductAttachments(attachments);
  };

  // Calculate default sales period (6 months, or full year if current month > July)
  const getDefaultSalesPeriod = () => {
    const now = dayjs();
    const currentMonth = now.month(); // 0-indexed (0 = January, 6 = July)

    if (currentMonth > 6) {
      // After July
      // Full year: January 1st to December 31st of current year
      return [dayjs().startOf("year"), dayjs().endOf("year")];
    } else {
      // 6 months back from today
      return [dayjs().subtract(6, "months").startOf("day"), dayjs().endOf("day")];
    }
  };

  const handleUpdateOrderStatus = async (order, newStatus) => {
    try {
      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "status_changed",
        user: email,
        oldValue: order.status,
        newValue: newStatus,
      });

      const updateData = {
        status: newStatus,
        log: newLog,
        lastModifiedBy: { email, updatedAt: dayjs().unix() },
      };

      if (newStatus === PURCHASE_ORDER_STATUS_COMPLETED) {
        updateData.completedAt = dayjs().unix();
      } else if (newStatus === PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION) {
        updateData.orderedAt = dayjs().unix();
        updateData.completedAt = null;
      } else if (newStatus === PURCHASE_ORDER_STATUS_RECEIVED) {
        updateData.receivedAt = dayjs().unix();
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(order.id).update(updateData);

      messageApi.success(t("notifications.purchase_order_status_update_successful"));
    } catch (error) {
      messageApi.error(t("notifications.purchase_order_status_update_failed"));
    }
  };

  const handleCancelOrder = async (order) => {
    const isDraft = order.status === PURCHASE_ORDER_STATUS_DRAFT;

    Modal.confirm({
      title: isDraft ? t("order.delete_purchase_order") : t("order.cancel_purchase_order"),
      content: isDraft ? t("order.delete_purchase_order_confirm") : t("order.cancel_purchase_order_confirm"),
      okText: t("yes"),
      cancelText: t("no"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          if (isDraft) {
            // Delete draft orders completely
            await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(order.id).delete();
            messageApi.success(t("notifications.purchase_order_deleted_successful"));
          } else {
            // Cancel non-draft orders
            const oldLog = order.log || [];
            const newLog = addLogEntry(oldLog, {
              action: "status_changed",
              user: email,
              oldValue: order.status,
              newValue: PURCHASE_ORDER_STATUS_CANCELLED,
            });

            await firestore
              .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
              .doc(order.id)
              .update({
                status: PURCHASE_ORDER_STATUS_CANCELLED,
                cancelledAt: dayjs().unix(),
                log: newLog,
                lastModifiedBy: { email, updatedAt: dayjs().unix() },
              });

            messageApi.success(t("notifications.purchase_order_cancelled_successful"));
          }
        } catch (error) {
          messageApi.error(
            isDraft
              ? t("notifications.purchase_order_deleted_failed")
              : t("notifications.purchase_order_cancelled_failed"),
          );
        }
      },
    });
  };

  const updateProductPricesAndPackaging = async (orderProducts) => {
    const updatePromises = orderProducts.map(async (orderProduct) => {
      const product = products.find((p) => p.id === orderProduct.productId);

      if (!product) return;

      const updates = {};
      let hasUpdates = false;

      // Check if purchase price differs
      if (orderProduct.price !== product.purchasePrice) {
        updates.purchasePrice = orderProduct.price;
        hasUpdates = true;
      }

      // Check if inner packaging quantity differs
      if (orderProduct.innerPackagingQuantity !== product.innerPackagingQuantity) {
        updates.innerPackagingQuantity = orderProduct.innerPackagingQuantity;
        hasUpdates = true;
      }

      // Check if master packaging quantity differs
      if (orderProduct.masterPackagingQuantity !== product.masterPackagingQuantity) {
        updates.masterPackagingQuantity = orderProduct.masterPackagingQuantity;
        hasUpdates = true;
      }

      // Only update if there are changes
      if (hasUpdates) {
        updates.updatedAt = dayjs().unix();
        updates.updatedBy = email;

        await firestore.collection("products").doc(orderProduct.productId).update(updates);
      }
    });

    await Promise.all(updatePromises);
  };

  const openEditModal = async (order) => {
    // Load sales data FIRST before loading the order
    const defaultPeriod = getDefaultSalesPeriod();
    setSalesPeriod(defaultPeriod);
    const salesData = await getProductSalesInPeriod(defaultPeriod[0].unix(), defaultPeriod[1].unix());
    setProductSalesData(salesData);
    // Load the order data into the form (this sets step to 2 if products exist)
    loadDraftOrder(order);

    setAddModalOpen(true);
  };

  const openAddModal = async () => {
    // Start fresh - drafts are visible in the table and can be opened from there
    const defaultPeriod = getDefaultSalesPeriod();
    setSalesPeriod(defaultPeriod);

    const startUnix = defaultPeriod[0].unix();
    const endUnix = defaultPeriod[1].unix();
    const salesData = await getProductSalesInPeriod(startUnix, endUnix);

    setProductSalesData(salesData);
    setProductSelectionStep(1);
    setSelectedProducts([]);
    setEditingOrder(null);
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    addForm.resetFields();
    setFileList([]);
    setProductAttachments({});
    setEditingOrder(null);
    setSelectedProducts([]);
    setDraftProducts([]);
    setProductSelectionStep(1);
    setSalesPeriod(null);
    setProductSalesData({});
    setProductSearchText("");
    setProductViewMode("table");
    setHasDraftRecovery(false);
  };

  const handleAddDraftProduct = () => {
    draftProductForm.validateFields().then((values) => {
      const newDraft = {
        tempId: `draft-${Date.now()}`,
        productName: values.productName,
        productCode: values.productCode,
        productBarcode: values.productBarcode || null,
        isDraft: true,
      };
      setDraftProducts([...draftProducts, newDraft]);
      draftProductForm.resetFields();
      setAddDraftProductModalOpen(false);
      messageApi.success(t("product_draft.add_draft_product"));
    });
  };

  const handleRemoveDraftProduct = (tempId) => {
    setDraftProducts(draftProducts.filter((d) => d.tempId !== tempId));
  };

  const createProductDraftsForOrder = async (orderProducts, purchaseOrderId, supplierId, userEmail) => {
    const updatedProducts = [];

    for (let i = 0; i < orderProducts.length; i++) {
      const product = orderProducts[i];

      // Check if this is a NEW draft product that needs a Firestore document created
      // Existing drafts have a Firestore ID (20+ alphanumeric chars), new ones have temp IDs like "draft-123456"
      const isNewDraft = product.isDraft && product.productDraftId?.startsWith("draft-");
      const isExistingDraft = product.isDraft && product.productDraftId && !product.productDraftId.startsWith("draft-");

      if (isNewDraft) {
        // This is a new draft product, create a ProductDraft document
        const draftData = {
          purchaseOrderId,
          purchaseOrderProductIndex: i,
          supplierId,
          productName: product.productName,
          productCode: product.productCode,
          productBarcode: product.productBarcode || null,
          draftProduct: {},
          status: PRODUCT_DRAFT_STATUS_NEEDS_SUPPLIER_INPUT,
          editableFields: [
            "purchasePrice",
            "innerPackagingQuantity",
            "masterPackagingQuantity",
            "length",
            "width",
            "height",
            "diameter",
            "color",
            "supplierMetadata",
          ],
          createdAt: dayjs().unix(),
          createdBy: userEmail,
        };

        const draftRef = await firestore.collection(FIRESTORE_PRODUCT_DRAFTS_TABLE).add(draftData);

        // Remove temporary fields and add real draft ID
        const { isDraft, draftTempId, draftProductName, draftProductCode, draftProductBarcode, ...cleanProduct } =
          product;
        updatedProducts.push({
          ...cleanProduct,
          productDraftId: draftRef.id,
        });
      } else if (isExistingDraft) {
        // Existing draft product - keep the productDraftId, clean up temporary fields
        const { isDraft, draftTempId, draftProductName, draftProductCode, draftProductBarcode, ...cleanProduct } =
          product;
        updatedProducts.push({
          ...cleanProduct,
          productDraftId: product.productDraftId, // Keep existing draft ID
        });
      } else {
        // Regular product - clean up any temporary fields
        const { isDraft, draftTempId, draftProductName, draftProductCode, draftProductBarcode, ...cleanProduct } =
          product;
        updatedProducts.push(cleanProduct);
      }
    }

    return updatedProducts;
  };

  // Helper function to get Transfer data source with category grouping
  const getTransferDataSource = () => {
    const selectedSupplierId = addForm.getFieldValue("supplier");
    const selectedSupplier = suppliers?.find((s) => s.id === selectedSupplierId);
    const hasSupplierProducts = selectedSupplier?.productIds && selectedSupplier.productIds.length > 0;

    if (!hasSupplierProducts || !products) return [];

    const supplierProducts = products.filter((p) => selectedSupplier.productIds.includes(p.id));

    const getSoldQuantity = (productId) => productSalesData[productId]?.soldQuantity || 0;

    const sortedBySales = supplierProducts.slice().sort((a, b) => {
      const aSales = getSoldQuantity(a.id);
      const bSales = getSoldQuantity(b.id);
      if (aSales !== bSales) return bSales - aSales;
      return (a.code || "").localeCompare(b.code || "");
    });

    const productById = new Map(sortedBySales.map((p) => [p.id, p]));

    const adjacency = new Map();
    const ensureAdj = (id) => {
      if (!adjacency.has(id)) adjacency.set(id, new Set());
      return adjacency.get(id);
    };

    sortedBySales.forEach((p) => {
      if (!p?.id) return;
      ensureAdj(p.id);
      const variantIds = Array.isArray(p.productVariants)
        ? p.productVariants.map((v) => v?.productId).filter(Boolean)
        : [];
      variantIds.forEach((variantId) => {
        if (!productById.has(variantId) || variantId === p.id) return;
        ensureAdj(p.id).add(variantId);
        ensureAdj(variantId).add(p.id);
      });
    });

    const visited = new Set();
    const groups = [];

    const sortByCode = (a, b) => {
      const aCode = a?.code || "";
      const bCode = b?.code || "";
      return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: "base" });
    };

    sortedBySales.forEach((p) => {
      if (!p?.id || visited.has(p.id)) return;

      const queue = [p.id];
      const componentIds = [];
      visited.add(p.id);

      while (queue.length) {
        const currentId = queue.shift();
        componentIds.push(currentId);
        const neighbors = adjacency.get(currentId);
        if (!neighbors) continue;
        neighbors.forEach((neighborId) => {
          if (visited.has(neighborId)) return;
          visited.add(neighborId);
          queue.push(neighborId);
        });
      }

      const items = componentIds
        .map((id) => productById.get(id))
        .filter(Boolean)
        .sort(sortByCode);
      const maxSold = items.reduce((max, item) => Math.max(max, getSoldQuantity(item.id)), 0);
      groups.push({ maxSold, items });
    });

    groups.sort((a, b) => {
      if (a.maxSold !== b.maxSold) return b.maxSold - a.maxSold;
      const aTop = a.items[0];
      const bTop = b.items[0];
      return (aTop?.code || "").localeCompare(bTop?.code || "");
    });

    const orderedProducts = [];
    const placed = new Set();
    groups.forEach((group) => {
      group.items.forEach((item) => {
        if (!item?.id || placed.has(item.id)) return;
        placed.add(item.id);
        orderedProducts.push(item);
      });
    });

    return orderedProducts.map((product) => ({
      key: product.id,
      title: `${product.name} ${product.code ? `(${product.code})` : ""}`,
      categoryName: product.category ? getCategoryName(product.category) : t("uncategorized"),
      code: product.code || "",
      stock: product.stock || 0,
      productId: product.id,
    }));
  };

  // Render function for Transfer items to show category, sales, stock and in order
  const renderTransferItem = (item) => {
    const salesData = productSalesData[item.productId];
    const soldQuantity = salesData?.soldQuantity || 0;
    const stock = item.stock || 0;
    const orderInfo = getProductQuantityInOrders(purchaseOrders, shipments, item.productId);
    const shippedInfo = getShippedQuantityInOrders(shipments, item.productId);

    return {
      label: (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>{item.title}</span>
          <div style={{ display: "flex", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
            <span style={{ fontSize: "12px", color: "#999" }}>{item.categoryName}</span>
            <span style={{ fontSize: "12px", color: "#1890ff" }}>Verkocht: {soldQuantity}</span>
            <span style={{ fontSize: "12px", color: stock > 0 ? "#52c41a" : "#ff4d4f" }}>Voorraad: {stock}</span>
            {orderInfo.total > 0 && (
              <span style={{ fontSize: "12px", color: "#fa8c16" }}>In bestelling: {orderInfo.total}</span>
            )}
            {shippedInfo.total > 0 && (
              <span style={{ fontSize: "12px", color: "#1890ff" }}>Verzonden: {shippedInfo.total}</span>
            )}
          </div>
        </div>
      ),
      value: item.title,
    };
  };

  // Helper function to get category name from category ID
  const getCategoryName = (categoryId) => {
    if (!categoryId || !categories) return t("uncategorized");

    const category = categories.find(
      (cat) => cat.categoryId === categoryId || cat.subcategories?.some((sub) => sub.subcategoryId === categoryId),
    );

    if (!category) return categoryId;

    // Check if it's a main category
    if (category.categoryId === categoryId) {
      return category.name;
    }

    // Check if it's a subcategory
    const subcategory = category.subcategories?.find((sub) => sub.subcategoryId === categoryId);
    if (subcategory) {
      return `${category.name} > ${subcategory.name}`;
    }

    return categoryId;
  };

  const onSearch = (e) => {
    setSearchText(e.target.value);
    setDisplayCount(20);
    setCurrentPage(1);
    updateSearchParams({ q: e.target.value || null, page: null });
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setDisplayCount(20);
    setCurrentPage(1);
    updateSearchParams({ status: value, page: null });
  };

  const handleOrderRowClick = (record) => {
    setSelectedOrder(record);
    setOrderDetailsModalOpen(true);
  };

  // Mobile card component
  const PurchaseOrderCard = ({ order }) => {
    const isChild = order._isChild || order.parentOrderId;

    return (
      <MobilePurchaseOrderCard
        onClick={() => navigate(`${urls.purchaseOrdersList}/${order.id}`)}
        style={{ marginLeft: isChild ? 32 : 0 }}
      >
        {isChild && <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>└─ Child Order</div>}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: 0, marginBottom: 4, fontSize: 16, fontWeight: 600 }}>
              {order.orderNumber}
              {order.isSplit && (
                <Tag color="magenta" style={{ marginLeft: 8 }}>
                  🔀 Split
                </Tag>
              )}
            </h4>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>{order.supplierName}</div>
            <div style={{ fontSize: 12, color: "#888" }}>{dayjs.unix(order.orderDate).format("DD-MM-YYYY")}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <Tag color={getPurchaseOrderStatusColor(order.status)}>{t(`order.${order.status.toLowerCase()}`)}</Tag>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1890ff" }}>
              {(() => {
                const additionalCostsTotal =
                  order.invoice?.additionalCosts?.reduce((sum, cost) => sum + cost.amount, 0) || 0;
                const productsTotal = order.totalPrice ?? 0;
                const total = productsTotal + additionalCostsTotal;
                return order.currency === "IDR" ? `IDR ${total.toLocaleString("id-ID")}` : formatPriceUsd(total);
              })()}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#666" }}>
          <span>{order.products?.length || 0} producten</span>
          {order.cubicMeters && <span>{order.cubicMeters} m³</span>}
          <Tag>{order.currency || "USD"}</Tag>
        </div>
      </MobilePurchaseOrderCard>
    );
  };

  const handleProductAttachmentToggle = (productId, checked) => {
    setProductAttachments((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        hasAttachments: checked,
        comment: checked ? prev[productId]?.comment || "" : undefined,
        fileList: checked ? prev[productId]?.fileList || [] : undefined,
      },
    }));
  };

  const handleProductCommentChange = (productId, comment) => {
    setProductAttachments((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        comment,
      },
    }));
  };

  const handleProductImagesChange = async (productId, { fileList: newFileList }) => {
    // Compress new files
    const processedFileList = await Promise.all(
      newFileList.map(async (file) => {
        if (file.originFileObj && !file.compressed) {
          const compressed = await compressImage(file.originFileObj, 0.7, 1200);
          return {
            ...file,
            originFileObj: compressed,
            compressed: true,
          };
        }
        return file;
      }),
    );

    setProductAttachments((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        fileList: processedFileList,
      },
    }));
  };

  // Filter and search logic
  const getFilteredOrders = () => {
    if (!purchaseOrders) return [];

    let filtered = [...purchaseOrders];

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.orderNumber?.toLowerCase().includes(searchLower) ||
          order.supplierName?.toLowerCase().includes(searchLower),
      );
    }

    // Apply supplier filter
    if (supplierFilter) {
      filtered = filtered.filter((order) => order.supplierId === supplierFilter);
    }

    // Apply status filter
    if (statusFilter && statusFilter !== "All") {
      if (statusFilter === "active") {
        // Hide cancelled and completed orders
        filtered = filtered.filter(
          (order) =>
            order.status !== PURCHASE_ORDER_STATUS_CANCELLED && order.status !== PURCHASE_ORDER_STATUS_COMPLETED,
        );
      } else {
        filtered = filtered.filter((order) => order.status === statusFilter);
      }
    }

    return filtered;
  };

  // Function to organize orders with children under parents, sorted by order number descending
  const organizeOrdersWithChildren = (orders) => {
    if (!orders) return [];

    const orderMap = new Map();
    const parentOrders = [];
    const childOrdersMap = new Map(); // Map parent ID to array of children

    // Separate parent and child orders
    orders.forEach((order) => {
      orderMap.set(order.id, order);
      if (order.parentOrderId) {
        if (!childOrdersMap.has(order.parentOrderId)) {
          childOrdersMap.set(order.parentOrderId, []);
        }
        childOrdersMap.get(order.parentOrderId).push(order);
      } else {
        parentOrders.push(order);
      }
    });

    // Sort parent orders by order number descending (newest first)
    parentOrders.sort((a, b) => {
      // Extract numeric parts for proper sorting (IO-20241209-45 -> 45)
      const getOrderNum = (orderNumber) => {
        if (!orderNumber) return 0;
        const parts = orderNumber.split("-");
        return parseInt(parts[parts.length - 1], 10) || 0;
      };
      const getOrderDate = (orderNumber) => {
        if (!orderNumber) return "";
        const parts = orderNumber.split("-");
        return parts.length >= 2 ? parts[1] : "";
      };

      // First sort by date part descending, then by number descending
      const dateA = getOrderDate(a.orderNumber);
      const dateB = getOrderDate(b.orderNumber);
      if (dateA !== dateB) {
        return dateB.localeCompare(dateA);
      }
      return getOrderNum(b.orderNumber) - getOrderNum(a.orderNumber);
    });

    // Build organized list with children directly under their parents
    const organized = [];
    parentOrders.forEach((parent) => {
      organized.push(parent);

      // Add children directly under this parent
      const children = childOrdersMap.get(parent.id) || [];
      // Sort children by their suffix (a, b, c, etc.)
      children.sort((a, b) => {
        const suffixA = a.orderNumber?.split("-").pop() || "";
        const suffixB = b.orderNumber?.split("-").pop() || "";
        return suffixA.localeCompare(suffixB);
      });
      children.forEach((child) => {
        organized.push({ ...child, _isChild: true });
      });
    });

    // Add orphaned children (children whose parent is not in the current list)
    childOrdersMap.forEach((children, parentId) => {
      if (!orderMap.has(parentId)) {
        children.forEach((child) => {
          organized.push({ ...child, _isChild: true });
        });
      }
    });

    return organized;
  };

  const tableColumns = [
    {
      title: t("order.order_id"),
      dataIndex: "orderNumber",
      // No sorter - order is fixed: newest first, children under parents
      render: (text, record) => {
        const orderLink =
          record.status === PURCHASE_ORDER_STATUS_CANCELLED ? (
            <span style={{ color: "#999" }}>{text}</span>
          ) : (
            <a onClick={() => navigate(`${urls.purchaseOrdersList}/${record.id}`)}>{text}</a>
          );

        const content = (
          <Space>
            {record.isSplit && <Tag color="magenta">🔀 Split</Tag>}
            {record.parentOrderId && <Tag color="purple">Child</Tag>}
            {orderLink}
          </Space>
        );

        // Add indent for child orders
        if (record._isChild || record.parentOrderId) {
          return (
            <div style={{ paddingLeft: 32, display: "flex", alignItems: "center" }}>
              <span style={{ marginRight: 8, color: "#999" }}>└─</span>
              {content}
            </div>
          );
        }

        return content;
      },
    },
    {
      title: t("supplier.supplier"),
      dataIndex: "supplierName",
    },
    {
      title: t("total_products"),
      dataIndex: "products",
      render: (products) => (products ? products.length : 0),
    },
    {
      title: t("total_price"),
      dataIndex: "totalPrice",
      render: (price, record) => {
        const additionalCostsTotal = record.invoice?.additionalCosts?.reduce((sum, cost) => sum + cost.amount, 0) || 0;
        const productsTotal = price || 0;
        const total = productsTotal + additionalCostsTotal;

        if (!total) return "-";
        const currency = record.currency || "USD";
        if (currency === "IDR") {
          return `IDR ${total.toLocaleString("id-ID")}`;
        }
        return formatPriceUsd(total);
      },
    },
    {
      title: t("currency"),
      dataIndex: "currency",
      render: (currency) => <Tag>{currency || "USD"}</Tag>,
    },
    {
      title: t("cubic_meters"),
      dataIndex: "cubicMeters",
      render: (meters) => (meters ? `${meters} m³` : ""),
    },
    {
      title: t("order.status"),
      dataIndex: "status",
      render: (status, record) => {
        const statusTag = <Tag color={getPurchaseOrderStatusColor(status)}>{t(`order.${status.toLowerCase()}`)}</Tag>;

        // Add badge for pending_approval
        if (status === PURCHASE_ORDER_STATUS_PENDING_APPROVAL) {
          return <Badge dot>{statusTag}</Badge>;
        }

        // Don't show Popover if status is ORDERED, SHIPPED, COMPLETED or CANCELLED
        if (
          status === PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION ||
          status === PURCHASE_ORDER_STATUS_PAID ||
          status === PURCHASE_ORDER_STATUS_IN_PRODUCTION ||
          status === PURCHASE_ORDER_STATUS_COMPLETED ||
          status === PURCHASE_ORDER_STATUS_CANCELLED
        ) {
          return statusTag;
        }

        return (
          <Popover
            title={
              <div style={{ textAlign: "center" }}>
                {status === PURCHASE_ORDER_STATUS_DRAFT &&
                  (() => {
                    // Check if order is complete enough to send to supplier
                    const hasProducts = record.products && record.products.length > 0;
                    const allProductsComplete =
                      hasProducts &&
                      record.products.every((p) => {
                        // Draft products only need amount > 0 (supplier fills in the rest)
                        if (p.productDraftId) {
                          return p.amount > 0;
                        }
                        // Regular products need all fields
                        return (
                          p.amount > 0 && p.innerPackagingQuantity > 0 && p.masterPackagingQuantity > 0 && p.price > 0
                        );
                      });
                    const canSendToSupplier = hasProducts && allProductsComplete;

                    let disabledReason = "";
                    if (!hasProducts) disabledReason = t("draft_needs_products");
                    else if (!allProductsComplete) disabledReason = t("draft_products_incomplete");

                    return (
                      <Button
                        type="primary"
                        size="small"
                        disabled={!canSendToSupplier}
                        onClick={() => handleUpdateOrderStatus(record, PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION)}
                        title={disabledReason}
                      >
                        {t("set_to_ordered")}
                      </Button>
                    );
                  })()}
              </div>
            }
          >
            {statusTag}
          </Popover>
        );
      },
    },
    {
      title: t("order.order_date"),
      dataIndex: "orderDate",
      render: (date) => dayjs.unix(date).format("DD-MM-YYYY"),
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, record) => {
        // Don't show actions for cancelled or completed orders
        if (record.status === PURCHASE_ORDER_STATUS_CANCELLED || record.status === PURCHASE_ORDER_STATUS_COMPLETED) {
          return null;
        }

        const menu = (
          <Menu>
            {record.status === PURCHASE_ORDER_STATUS_DRAFT && (
              <Menu.Item onClick={() => openEditModal(record)}>{t("edit")}</Menu.Item>
            )}
            <Menu.Item onClick={() => handleCancelOrder(record)}>
              <span style={{ color: "#ff4d4f" }}>{t("order.cancel_order")}</span>
            </Menu.Item>
          </Menu>
        );

        return <EllipsisDropdown menu={menu} />;
      },
    },
  ];

  const filteredOrders = getFilteredOrders();
  const organizedOrders = organizeOrdersWithChildren(filteredOrders);

  return (
    <>
      {contextHolder}
      <h2>{t("order.purchase_orders")}</h2>
      <Card>
        {isMobile ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <Input
                placeholder={t("search")}
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={onSearch}
                style={{ marginBottom: 8 }}
              />
              <Select
                allowClear
                placeholder={t("supplier.supplier")}
                value={supplierFilter}
                onChange={(v) => {
                  setSupplierFilter(v);
                  setDisplayCount(20);
                  setCurrentPage(1);
                  updateSearchParams({ supplier: v, page: null });
                }}
                style={{ width: "100%", marginBottom: 8 }}
                options={(suppliers || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((s) => ({ value: s.id, label: s.name || s.id }))}
              />
              <Select
                value={statusFilter}
                onChange={handleStatusFilter}
                style={{ width: "100%", marginBottom: 8 }}
                options={[
                  { value: "active", label: "Actief (zonder geannuleerd/afgerond)" },
                  { value: "All", label: t("all") },
                  {
                    value: PURCHASE_ORDER_STATUS_DRAFT,
                    label: t(`order.${PURCHASE_ORDER_STATUS_DRAFT.toLowerCase()}`),
                  },
                  {
                    value: PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION,
                    label: t(`order.${PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION.toLowerCase()}`),
                  },
                  {
                    value: PURCHASE_ORDER_STATUS_PENDING_APPROVAL,
                    label: t(`order.${PURCHASE_ORDER_STATUS_PENDING_APPROVAL.toLowerCase()}`),
                  },
                  // {
                  //   value: PURCHASE_ORDER_STATUS_PAYMENT_PENDING,
                  //   label: t(`order.${PURCHASE_ORDER_STATUS_PAYMENT_PENDING.toLowerCase()}`),
                  // },
                  // { value: PURCHASE_ORDER_STATUS_PAID, label: t(`order.${PURCHASE_ORDER_STATUS_PAID.toLowerCase()}`) },
                  {
                    value: PURCHASE_ORDER_STATUS_IN_PRODUCTION,
                    label: t(`order.${PURCHASE_ORDER_STATUS_IN_PRODUCTION.toLowerCase()}`),
                  },
                  {
                    value: PURCHASE_ORDER_STATUS_SHIPPED,
                    label: t(`order.${PURCHASE_ORDER_STATUS_SHIPPED.toLowerCase()}`),
                  },
                  {
                    value: PURCHASE_ORDER_STATUS_RECEIVED,
                    label: t(`order.${PURCHASE_ORDER_STATUS_RECEIVED.toLowerCase()}`),
                  },
                  {
                    value: PURCHASE_ORDER_STATUS_COMPLETED,
                    label: t(`order.${PURCHASE_ORDER_STATUS_COMPLETED.toLowerCase()}`),
                  },
                  {
                    value: PURCHASE_ORDER_STATUS_CANCELLED,
                    label: t(`order.${PURCHASE_ORDER_STATUS_CANCELLED.toLowerCase()}`),
                  },
                ]}
              />
              <Button type="primary" onClick={openAddModal} style={{ width: "100%" }}>
                {t("order.add_purchase_order")}
              </Button>
            </div>
            <div style={{ padding: "8px 0" }}>
              {organizedOrders && organizedOrders.length > 0 ? (
                <>
                  <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    {organizedOrders.slice(0, displayCount).map((order) => (
                      <PurchaseOrderCard key={order.id} order={order} />
                    ))}
                  </Space>
                  {displayCount < organizedOrders.length && (
                    <div style={{ textAlign: "center", padding: "20px 0" }}>
                      <Button
                        type="default"
                        onClick={() => setDisplayCount((prev) => Math.min(prev + 20, organizedOrders.length))}
                        style={{ minWidth: 200 }}
                      >
                        Laad meer ({organizedOrders.length - displayCount} resterend)
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <Card>
                  <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>{t("no_data")}</div>
                </Card>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="mb-2" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Space>
                <Input placeholder={t("search")} prefix={<SearchOutlined />} value={searchText} onChange={onSearch} />
                <Select
                  allowClear
                  placeholder={t("supplier.supplier")}
                  value={supplierFilter}
                  onChange={(v) => {
                    setSupplierFilter(v);
                    setCurrentPage(1);
                    updateSearchParams({ supplier: v, page: null });
                  }}
                  style={{ minWidth: 200 }}
                  options={(suppliers || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((s) => ({ value: s.id, label: s.name || s.id }))}
                />
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  style={{ minWidth: 200 }}
                  options={[
                    { value: "active", label: "Actief (zonder geannuleerd/afgerond)" },
                    { value: "All", label: t("all") },
                    {
                      value: PURCHASE_ORDER_STATUS_DRAFT,
                      label: t(`order.${PURCHASE_ORDER_STATUS_DRAFT.toLowerCase()}`),
                    },
                    {
                      value: PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION,
                      label: t(`order.${PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION.toLowerCase()}`),
                    },
                    {
                      value: PURCHASE_ORDER_STATUS_PENDING_APPROVAL,
                      label: t(`order.${PURCHASE_ORDER_STATUS_PENDING_APPROVAL.toLowerCase()}`),
                    },
                    // {
                    //   value: PURCHASE_ORDER_STATUS_PAYMENT_PENDING,
                    //   label: t(`order.${PURCHASE_ORDER_STATUS_PAYMENT_PENDING.toLowerCase()}`),
                    // },
                    // {
                    //   value: PURCHASE_ORDER_STATUS_PAID,
                    //   label: t(`order.${PURCHASE_ORDER_STATUS_PAID.toLowerCase()}`),
                    // },
                    {
                      value: PURCHASE_ORDER_STATUS_IN_PRODUCTION,
                      label: t(`order.${PURCHASE_ORDER_STATUS_IN_PRODUCTION.toLowerCase()}`),
                    },
                    {
                      value: PURCHASE_ORDER_STATUS_SHIPPED,
                      label: t(`order.${PURCHASE_ORDER_STATUS_SHIPPED.toLowerCase()}`),
                    },
                    {
                      value: PURCHASE_ORDER_STATUS_RECEIVED,
                      label: t(`order.${PURCHASE_ORDER_STATUS_RECEIVED.toLowerCase()}`),
                    },
                    {
                      value: PURCHASE_ORDER_STATUS_COMPLETED,
                      label: t(`order.${PURCHASE_ORDER_STATUS_COMPLETED.toLowerCase()}`),
                    },
                    {
                      value: PURCHASE_ORDER_STATUS_CANCELLED,
                      label: t(`order.${PURCHASE_ORDER_STATUS_CANCELLED.toLowerCase()}`),
                    },
                  ]}
                />
              </Space>
              <Button type="primary" onClick={openAddModal}>
                {t("order.add_purchase_order")}
              </Button>
            </div>
            <Table
              columns={tableColumns}
              dataSource={organizedOrders}
              rowKey="id"
              scroll={{ x: true }}
              showSorterTooltip={false}
              style={{ marginTop: 20 }}
              pagination={{
                current: currentPage,
                onChange: (page) => {
                  setCurrentPage(page);
                  updateSearchParams({ page: String(page) });
                },
              }}
            />
          </>
        )}
      </Card>

      <Modal
        open={addModalOpen}
        title={
          editingOrder
            ? t("order.edit_purchase_order")
            : productSelectionStep === 1
              ? t("step1_select_products")
              : t("step2_enter_details")
        }
        onCancel={() => closeAddModal()}
        width={productSelectionStep === 1 ? 1200 : 800}
        footer={
          productSelectionStep === 1
            ? // Footer for step 1 (product selection)
              [
                <Button key="cancel" onClick={() => closeAddModal()}>
                  {t("cancel")}
                </Button>,
                <Button key="save" onClick={handleSaveDraftToDatabase} loading={isSubmitting}>
                  {t("save")}
                </Button>,
                <Button
                  key="next"
                  type="primary"
                  onClick={() => {
                    if (selectedProducts.length === 0 && draftProducts.length === 0) {
                      messageApi.error(t("select_min_one_product"));
                      return;
                    }
                    setProductSelectionStep(2);
                    // Initialize form with selected products and their data, preserving existing values
                    const existingProducts = addForm.getFieldValue("products") || [];
                    const existingProductMap = {};
                    existingProducts.forEach((p) => {
                      if (p?.product) {
                        existingProductMap[p.product] = p;
                      }
                    });

                    // Combine existing products with draft products
                    const existingProductsForm = selectedProducts.map((productId) => {
                      // If this product already has form data, preserve it
                      if (existingProductMap[productId]) {
                        return existingProductMap[productId];
                      }
                      // Otherwise, initialize with default values from product
                      const product = products?.find((p) => p.id === productId);
                      return {
                        product: productId,
                        price: product?.purchasePrice,
                        innerPackagingQuantity: product?.innerPackagingQuantity,
                        masterPackagingQuantity: product?.masterPackagingQuantity,
                        priority: false,
                      };
                    });

                    // Add draft products to the form
                    const draftProductsForm = draftProducts.map((draft) => ({
                      product: null,
                      isDraft: true,
                      draftTempId: draft.tempId,
                      draftProductName: draft.productName,
                      draftProductCode: draft.productCode,
                      draftProductBarcode: draft.productBarcode,
                      price: 0,
                      amount: 0,
                      priority: false,
                    }));

                    addForm.setFieldsValue({
                      products: [...existingProductsForm, ...draftProductsForm],
                    });
                  }}
                >
                  {t("next")} →
                </Button>,
              ]
            : // Footer for step 2 (enter details) or editing mode
              [
                <Button key="back" onClick={() => setProductSelectionStep(1)}>
                  ← {t("back")}
                </Button>,
                <Button key="cancel" onClick={() => closeAddModal()}>
                  {t("cancel")}
                </Button>,
                <Button key="save" type="primary" onClick={handleSaveDraftToDatabase} loading={isSubmitting}>
                  {t("save")}
                </Button>,
              ]
        }
      >
        {productSelectionStep === 1 ? (
          /* STEP 1: Product Selection */
          <div>
            <Form form={addForm} layout="vertical">
              <Form.Item
                name="supplier"
                label={t("supplier.supplier")}
                rules={[{ required: true, message: t("please_select_supplier") }]}
              >
                <Select
                  showSearch
                  placeholder={t("select_supplier")}
                  optionFilterProp="label"
                  filterOption={(input, option) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  loading={suppliersLoading}
                  onChange={async (supplierId) => {
                    setSelectedProducts([]);
                    // Recalculate sales data when supplier changes
                    if (salesPeriod) {
                      const startUnix = salesPeriod[0].unix();
                      const endUnix = salesPeriod[1].unix();
                      const salesData = await getProductSalesInPeriod(startUnix, endUnix);
                      setProductSalesData(salesData);
                    }
                  }}
                >
                  {suppliers?.map((supplier) => (
                    <Select.Option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Verkoop periode">
                <DatePicker.RangePicker
                  value={salesPeriod}
                  onChange={async (dates) => {
                    setSalesPeriod(dates);
                    if (dates) {
                      // Recalculate sales data using the hook
                      const startUnix = dates[0].unix();
                      const endUnix = dates[1].unix();
                      const salesData = await getProductSalesInPeriod(startUnix, endUnix);
                      setProductSalesData(salesData);
                    }
                  }}
                  presets={[
                    {
                      label: "3 maanden",
                      value: [dayjs().subtract(3, "months").startOf("day"), dayjs().endOf("day")],
                    },
                    {
                      label: "6 maanden",
                      value: [dayjs().subtract(6, "months").startOf("day"), dayjs().endOf("day")],
                    },
                    {
                      label: "1 jaar",
                      value: [dayjs().subtract(1, "year").startOf("day"), dayjs().endOf("day")],
                    },
                    {
                      label: "Dit jaar",
                      value: [dayjs().startOf("year"), dayjs().endOf("day")],
                    },
                  ]}
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                />
                <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
                  {salesPeriod &&
                    `Periode: ${salesPeriod[0].format("DD-MM-YYYY")} t/m ${salesPeriod[1].format("DD-MM-YYYY")}`}
                </div>
              </Form.Item>
            </Form>

            {addForm.getFieldValue("supplier") && (
              <div style={{ marginTop: 24 }}>
                <div
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}
                >
                  <h6 style={{ margin: 0 }}>
                    Selecteer producten ({selectedProducts.length + draftProducts.length} geselecteerd)
                  </h6>
                  <Space>
                    <Button icon={<FileAddOutlined />} onClick={() => setAddDraftProductModalOpen(true)}>
                      {t("product_draft.add_draft_product")}
                    </Button>
                    {productViewMode === "table" && (
                      <Input
                        placeholder={t("search")}
                        prefix={<SearchOutlined />}
                        value={productSearchText}
                        onChange={(e) => setProductSearchText(e.target.value)}
                        style={{ width: 250 }}
                        allowClear
                      />
                    )}
                    <Segmented
                      value={productViewMode}
                      onChange={setProductViewMode}
                      options={[
                        { value: "table", icon: <UnorderedListOutlined /> },
                        { value: "transfer", icon: <AppstoreOutlined /> },
                      ]}
                    />
                  </Space>
                </div>

                {/* Draft Products Section */}
                {draftProducts.length > 0 && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: 16,
                      background: "#fffbe6",
                      border: "1px solid #ffe58f",
                      borderRadius: 8,
                    }}
                  >
                    <h5 style={{ margin: "0 0 12px 0" }}>
                      <FileAddOutlined style={{ marginRight: 8 }} />
                      Nieuwe producten (drafts) - {draftProducts.length}
                    </h5>
                    <Space wrap>
                      {draftProducts.map((draft) => (
                        <Tag
                          key={draft.tempId}
                          closable
                          onClose={() => handleRemoveDraftProduct(draft.tempId)}
                          color="gold"
                          style={{ padding: "4px 8px" }}
                        >
                          <strong>{draft.productName}</strong>
                          <span style={{ marginLeft: 8, color: "#666" }}>({draft.productCode})</span>
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}

                {productViewMode === "table" ? (
                  <Table
                    key={productTableKey}
                    dataSource={(() => {
                      const selectedSupplierId = addForm.getFieldValue("supplier");
                      const selectedSupplier = suppliers.find((s) => s.id === selectedSupplierId);
                      const hasSupplierProducts =
                        selectedSupplier?.productIds && selectedSupplier.productIds.length > 0;
                      let supplierProducts = hasSupplierProducts
                        ? products?.filter((p) => selectedSupplier.productIds.includes(p.id))
                        : [];

                      // Apply search filter
                      if (productSearchText) {
                        const searchLower = productSearchText.toLowerCase();
                        supplierProducts = supplierProducts.filter(
                          (p) =>
                            p.name?.toLowerCase().includes(searchLower) || p.code?.toLowerCase().includes(searchLower),
                        );
                      }

                      const getSoldQuantity = (productId) => productSalesData[productId]?.soldQuantity || 0;

                      // Sort by sold quantity (desc) and group variants directly under their parent product
                      const sortedBySales = supplierProducts.slice().sort((a, b) => {
                        const aSales = getSoldQuantity(a.id);
                        const bSales = getSoldQuantity(b.id);
                        if (aSales !== bSales) return bSales - aSales;
                        return (a.code || "").localeCompare(b.code || "");
                      });

                      const productById = new Map(sortedBySales.map((p) => [p.id, p]));

                      // Build an adjacency list for variant relationships (productVariants is symmetric in this project)
                      const adjacency = new Map();
                      const ensureAdj = (id) => {
                        if (!adjacency.has(id)) adjacency.set(id, new Set());
                        return adjacency.get(id);
                      };

                      sortedBySales.forEach((p) => {
                        if (!p?.id) return;
                        ensureAdj(p.id);
                        const variantIds = Array.isArray(p.productVariants)
                          ? p.productVariants.map((v) => v?.productId).filter(Boolean)
                          : [];
                        variantIds.forEach((variantId) => {
                          if (!productById.has(variantId) || variantId === p.id) return;
                          ensureAdj(p.id).add(variantId);
                          ensureAdj(variantId).add(p.id);
                        });
                      });

                      // Compute connected components (variant groups)
                      const visited = new Set();
                      const groups = [];

                      const sortByCode = (a, b) => {
                        const aCode = a?.code || "";
                        const bCode = b?.code || "";
                        return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: "base" });
                      };

                      sortedBySales.forEach((p) => {
                        if (!p?.id || visited.has(p.id)) return;

                        const queue = [p.id];
                        const componentIds = [];
                        visited.add(p.id);

                        while (queue.length) {
                          const currentId = queue.shift();
                          componentIds.push(currentId);
                          const neighbors = adjacency.get(currentId);
                          if (!neighbors) continue;
                          neighbors.forEach((neighborId) => {
                            if (visited.has(neighborId)) return;
                            visited.add(neighborId);
                            queue.push(neighborId);
                          });
                        }

                        const items = componentIds
                          .map((id) => productById.get(id))
                          .filter(Boolean)
                          .sort(sortByCode);

                        const maxSold = items.reduce((max, item) => Math.max(max, getSoldQuantity(item.id)), 0);
                        groups.push({ maxSold, items });
                      });

                      // Sort groups by their best-selling item, then flatten
                      groups.sort((a, b) => {
                        if (a.maxSold !== b.maxSold) return b.maxSold - a.maxSold;
                        const aTop = a.items[0];
                        const bTop = b.items[0];
                        return (aTop?.code || "").localeCompare(bTop?.code || "");
                      });

                      const result = [];
                      const placed = new Set();
                      groups.forEach((group) => {
                        group.items.forEach((item) => {
                          if (!item?.id || placed.has(item.id)) return;
                          placed.add(item.id);
                          result.push(item);
                        });
                      });

                      return result;
                    })()}
                    rowKey="id"
                    rowSelection={{
                      selectedRowKeys: selectedProducts,
                      preserveSelectedRowKeys: true,
                      onChange: (selectedRowKeys) => {
                        setSelectedProducts(selectedRowKeys);
                      },
                    }}
                    columns={[
                      {
                        title: t("category"),
                        dataIndex: "category",
                        key: "category",
                        width: 180,
                        render: (category) => (
                          <span style={{ fontSize: 12, color: "#666" }}>{getCategoryName(category)}</span>
                        ),
                        sorter: (a, b) => getCategoryName(a.category).localeCompare(getCategoryName(b.category)),
                      },
                      {
                        title: "Product",
                        dataIndex: "name",
                        key: "name",
                        render: (text, record) => (
                          <div>
                            <div>
                              <strong>{text}</strong>
                            </div>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              {record.code} {record.color && `• ${getFormattedProductColor(record.color)}`}
                            </div>
                          </div>
                        ),
                      },
                      {
                        title: (
                          <div style={{ position: "relative" }}>
                            {t("sold_period")}{" "}
                            {productViewMode === "table" && (
                              <Button
                                onClick={() => setProductTableKey((k) => k + 1)}
                                icon={<ReloadOutlined style={{ fontSize: 12 }} />}
                                size={"small"}
                                style={{ position: "absolute", top: 0, left: 70 }}
                              />
                            )}
                          </div>
                        ),
                        dataIndex: "id",
                        key: "sold",
                        width: 150,
                        defaultSortOrder: "descend",
                        sorter: false,
                        render: (id) => {
                          const salesData = productSalesData[id];
                          return salesData ? (
                            <Tag color="blue">
                              {salesData.soldQuantity} {t("pieces")}
                            </Tag>
                          ) : (
                            <Tag>0 {t("pieces")}</Tag>
                          );
                        },
                      },
                      {
                        title: t("stock"),
                        dataIndex: "stock",
                        key: "stock",
                        width: 120,
                        sorter: (a, b) => (a.stock || 0) - (b.stock || 0),
                        render: (stock) => (
                          <Tag color={stock > 0 ? "green" : "red"}>
                            {stock || 0} {t("pieces")}
                          </Tag>
                        ),
                      },
                      {
                        title: t("in_order"),
                        dataIndex: "id",
                        key: "inOrder",
                        width: 150,
                        render: (id) => {
                          if (!purchaseOrders || shipmentsLoading) {
                            return <Spin size="small" />;
                          }
                          const orderInfo = getProductQuantityInOrders(purchaseOrders, shipments, id);
                          if (orderInfo.total === 0) {
                            return <Tag>-</Tag>;
                          }
                          return (
                            <div>
                              <Tag color="orange">
                                {orderInfo.total} {t("pieces")}
                              </Tag>
                              <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                                {orderInfo.orders.map((o) => `${o.orderNumber} (${o.amount}x)`).join(", ")}
                              </div>
                            </div>
                          );
                        },
                      },
                      {
                        title: t("shipped"),
                        dataIndex: "id",
                        key: "shipped",
                        width: 150,
                        render: (id) => {
                          if (shipmentsLoading) {
                            return <Spin size="small" />;
                          }
                          const shippedInfo = getShippedQuantityInOrders(shipments, id);
                          if (shippedInfo.total === 0) {
                            return <Tag>-</Tag>;
                          }
                          return (
                            <div>
                              <Tag color="blue">
                                {shippedInfo.total} {t("pieces")}
                              </Tag>
                              <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                                {shippedInfo.shipments.map((s) => `${s.shipmentNumber} (${s.amount}x)`).join(", ")}
                              </div>
                            </div>
                          );
                        },
                      },
                    ]}
                    pagination={{
                      defaultPageSize: 10,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                    }}
                    scroll={{ y: 400 }}
                  />
                ) : (
                  <Transfer
                    dataSource={getTransferDataSource()}
                    showSearch
                    filterOption={(inputValue, item) =>
                      item.title.toLowerCase().includes(inputValue.toLowerCase()) ||
                      item.code.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    listStyle={{
                      width: "100%",
                      height: 400,
                    }}
                    targetKeys={selectedProducts}
                    onChange={(targetKeys) => {
                      setSelectedProducts(targetKeys);
                    }}
                    render={renderTransferItem}
                    titles={[t("available_products"), t("selected_products")]}
                    locale={{
                      itemUnit: t("product"),
                      itemsUnit: t("products"),
                      searchPlaceholder: t("search"),
                    }}
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          /* STEP 2: Enter Details */
          <Form form={addForm} layout="vertical">
            <Form.Item
              name="supplier"
              label={t("supplier.supplier")}
              rules={[{ required: true, message: t("please_select_supplier") }]}
            >
              <Select
                showSearch
                placeholder={t("select_supplier")}
                optionFilterProp="label"
                filterOption={(input, option) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                loading={suppliersLoading}
                onChange={() => {
                  // Clear products when supplier changes
                  addForm.setFieldsValue({ products: [] });
                }}
              >
                {suppliers?.map((supplier) => (
                  <Select.Option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.List name="products">
              {(fields, { add, remove }) => {
                // Get all currently selected product IDs
                const selectedProductIds =
                  addForm
                    .getFieldValue("products")
                    ?.map((p) => p?.product)
                    .filter(Boolean) || [];

                // Get selected supplier and filter products
                const selectedSupplierId = addForm.getFieldValue("supplier");
                const selectedSupplier = suppliers.find((s) => s.id === selectedSupplierId);
                const hasSupplierProducts = selectedSupplier?.productIds && selectedSupplier.productIds.length > 0;
                const availableProducts = hasSupplierProducts
                  ? products?.filter((p) => selectedSupplier.productIds.includes(p.id))
                  : [];

                return (
                  <>
                    {selectedSupplierId && !hasSupplierProducts && (
                      <div
                        style={{
                          padding: "16px",
                          marginBottom: "16px",
                          background: "#fff7e6",
                          border: "1px solid #ffd591",
                          borderRadius: "4px",
                        }}
                      >
                        <p style={{ margin: 0, marginBottom: 8 }}>
                          ⚠️ <strong>Geen producten gekoppeld aan deze leverancier</strong>
                        </p>
                        <p style={{ margin: 0, marginBottom: 8, fontSize: "14px" }}>
                          Koppel eerst producten aan deze leverancier voordat je een inkooporder kunt aanmaken.
                        </p>
                        <Button type="primary" size="small" onClick={() => navigate(urls.suppliersList)}>
                          Ga naar Leveranciers
                        </Button>
                      </div>
                    )}
                    <Collapse defaultActiveKey={fields.length > 0 ? [fields[0].key] : []} style={{ marginBottom: 16 }}>
                      {fields.map((field, index) => {
                        // Get the current field's selected product
                        const currentProductId = addForm.getFieldValue(["products", field.name, "product"]);
                        const currentProduct = products.find((p) => p.id === currentProductId);

                        // Check if this is a draft product
                        const isDraftProduct = addForm.getFieldValue(["products", field.name, "isDraft"]);
                        const draftProductName = addForm.getFieldValue(["products", field.name, "draftProductName"]);
                        const draftProductCode = addForm.getFieldValue(["products", field.name, "draftProductCode"]);

                        // Get non-completed orders for this product
                        const ordersWithProduct = getProductInNonCompletedOrders(
                          purchaseOrders,
                          currentProductId,
                          editingOrder,
                        );
                        const productQuantityInfo = getProductQuantityInOrders(
                          purchaseOrders,
                          shipments,
                          currentProductId,
                          editingOrder,
                        );
                        const shippedInfo = getShippedQuantityInOrders(shipments, currentProductId);
                        const hasWarning = ordersWithProduct.length > 0 && productQuantityInfo.total > 0;

                        // Check if attachments are enabled for this product
                        const hasAttachments = productAttachments[currentProductId]?.hasAttachments || false;

                        // Extract key from field to avoid React warning
                        const { key, ...fieldProps } = field;

                        // Get priority status
                        const isPriority = addForm.getFieldValue(["products", field.name, "priority"]);

                        // Check if all required fields are filled for this product
                        const productAmount = addForm.getFieldValue(["products", field.name, "amount"]);
                        const productInnerQty = addForm.getFieldValue([
                          "products",
                          field.name,
                          "innerPackagingQuantity",
                        ]);
                        const productMasterQty = addForm.getFieldValue([
                          "products",
                          field.name,
                          "masterPackagingQuantity",
                        ]);
                        const productPrice = addForm.getFieldValue(["products", field.name, "price"]);

                        // Draft products don't need all fields filled - supplier will do that
                        const isProductComplete = isDraftProduct
                          ? true
                          : currentProductId &&
                            productAmount > 0 &&
                            productInnerQty > 0 &&
                            productMasterQty > 0 &&
                            productPrice > 0;

                        // Create panel header with product info
                        const panelHeader = (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Badge color={isProductComplete ? "green" : "red"} />
                            <span>
                              {isDraftProduct
                                ? `${index + 1}. ${draftProductName} (${draftProductCode})`
                                : currentProduct
                                  ? `${index + 1}. ${currentProduct.name} (${currentProduct.code})`
                                  : `${index + 1}. ${t("select_a_product")}`}
                            </span>
                            {isDraftProduct && (
                              <Tag color="gold" style={{ margin: 0 }}>
                                {t("product_draft.new_product")}
                              </Tag>
                            )}
                            {isPriority && (
                              <Tag color="red" style={{ margin: 0 }}>
                                🔥 {t("priority")}
                              </Tag>
                            )}
                            {hasWarning && (
                              <Tag color="warning" style={{ margin: 0 }}>
                                ⚠️ {t("in_order")}
                              </Tag>
                            )}
                          </div>
                        );

                        return (
                          <Collapse.Panel
                            key={key}
                            header={panelHeader}
                            extra={
                              <MinusCircleOutlined
                                onClick={(e) => {
                                  e.stopPropagation();
                                  remove(field.name);
                                }}
                                style={{ color: "#ff4d4f", cursor: "pointer" }}
                              />
                            }
                          >
                            {/* Draft Product Info Banner */}
                            {isDraftProduct && (
                              <div
                                style={{
                                  background: "#fffbe6",
                                  padding: "12px 16px",
                                  borderRadius: 6,
                                  marginBottom: 16,
                                  border: "1px solid #ffe58f",
                                }}
                              >
                                <div style={{ fontWeight: 600, marginBottom: 8, color: "#d48806" }}>
                                  {t("product_draft.new_product")} - {t("product_draft.supplier_fills_details")}
                                </div>
                                <Row gutter={16}>
                                  <Col span={6}>
                                    <span style={{ color: "#666", fontSize: 12 }}>{t("product_name")}</span>
                                    <div style={{ fontWeight: 500 }}>{draftProductName}</div>
                                  </Col>
                                  <Col span={6}>
                                    <span style={{ color: "#666", fontSize: 12 }}>
                                      {t("product_draft.draft_product_code")}
                                    </span>
                                    <div style={{ fontWeight: 500 }}>{draftProductCode}</div>
                                  </Col>
                                  <Col span={6}>
                                    <span style={{ color: "#666", fontSize: 12 }}>{t("barcode")}</span>
                                    <div style={{ fontWeight: 500 }}>
                                      {addForm.getFieldValue(["products", field.name, "draftProductBarcode"]) || "-"}
                                    </div>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item
                                      {...fieldProps}
                                      name={[field.name, "amount"]}
                                      label={t("order.amount")}
                                      rules={[{ required: true, message: t("form.enter_value") }]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <InputNumber min={1} placeholder="0" style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </div>
                            )}

                            {/* Product Info Banner */}
                            {currentProductId && !isDraftProduct && (
                              <div
                                style={{
                                  background: "#f6f8fa",
                                  padding: "12px 16px",
                                  borderRadius: 6,
                                  marginBottom: 16,
                                  display: "flex",
                                  gap: 24,
                                  flexWrap: "wrap",
                                }}
                              >
                                <div>
                                  <span style={{ color: "#666", fontSize: 12 }}>{t("sold_period")}</span>
                                  <div style={{ fontWeight: 600, color: "#1890ff", fontSize: 16 }}>
                                    {productSalesData[currentProductId]?.soldQuantity || 0} {t("pieces")}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: "#666", fontSize: 12 }}>{t("current_stock")}</span>
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      fontSize: 16,
                                      color: (currentProduct?.stock || 0) > 0 ? "#52c41a" : "#ff4d4f",
                                    }}
                                  >
                                    {currentProduct?.stock || 0} {t("pieces")}
                                  </div>
                                </div>
                                {productQuantityInfo.total > 0 && (
                                  <div>
                                    <span style={{ color: "#666", fontSize: 12 }}>{t("in_order")}</span>
                                    <div style={{ fontWeight: 600, color: "#fa8c16", fontSize: 16 }}>
                                      {productQuantityInfo.total} {t("pieces")}
                                    </div>
                                  </div>
                                )}
                                {shippedInfo.total > 0 && (
                                  <div>
                                    <span style={{ color: "#666", fontSize: 12 }}>{t("shipped")}</span>
                                    <div style={{ fontWeight: 600, color: "#1890ff", fontSize: 16 }}>
                                      {shippedInfo.total} {t("pieces")}
                                    </div>
                                  </div>
                                )}
                                {salesPeriod && (
                                  <div style={{ marginLeft: "auto", alignSelf: "center" }}>
                                    <span style={{ color: "#999", fontSize: 11 }}>
                                      {t("period")}: {salesPeriod[0].format("DD-MM-YYYY")} -{" "}
                                      {salesPeriod[1].format("DD-MM-YYYY")}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Product Selection Row - Only for non-draft products */}
                            {!isDraftProduct && (
                              <Row gutter={[16, 8]}>
                                <Col xs={24} md={16}>
                                  <Form.Item
                                    {...fieldProps}
                                    name={[field.name, "product"]}
                                    label={t("product")}
                                    rules={[{ required: true, message: t("please_select_a_product") }]}
                                    help={
                                      hasWarning && (
                                        <span style={{ color: "#faad14" }}>
                                          ⚠️ Dit product zit in {ordersWithProduct.length} andere bestelling
                                          {ordersWithProduct.length > 1 ? "en" : ""}: Openstaand:{" "}
                                          {productQuantityInfo.total}
                                          {shippedInfo.total > 0 && ` (Verzonden: ${shippedInfo.total})`}
                                        </span>
                                      )
                                    }
                                    validateStatus={hasWarning ? "warning" : undefined}
                                    style={{ marginBottom: 8 }}
                                  >
                                    <Select
                                      showSearch
                                      placeholder={t("select_a_product")}
                                      optionFilterProp="label"
                                      filterOption={(input, option) => {
                                        const product = availableProducts?.find((p) => p.id === option.value);
                                        if (!product) return false;
                                        const searchStr = `${product.name} ${product.code}`.toLowerCase();
                                        return searchStr.includes(input.toLowerCase());
                                      }}
                                      onChange={(productId) => {
                                        const selectedProduct = availableProducts?.find((p) => p.id === productId);
                                        if (selectedProduct) {
                                          // Auto-fill fields from product data
                                          const currentProducts = addForm.getFieldValue("products") || [];
                                          currentProducts[field.name] = {
                                            ...currentProducts[field.name],
                                            product: productId,
                                            innerPackagingQuantity: selectedProduct.innerPackagingQuantity || undefined,
                                            masterPackagingQuantity:
                                              selectedProduct.masterPackagingQuantity || undefined,
                                            price: selectedProduct.purchasePrice || undefined,
                                          };
                                          addForm.setFieldsValue({ products: currentProducts });
                                        }
                                      }}
                                    >
                                      {availableProducts
                                        ?.filter(
                                          (product) =>
                                            !selectedProductIds.includes(product.id) || product.id === currentProductId,
                                        )
                                        .map((product) => (
                                          <Select.Option key={product.id} value={product.id}>
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                              <div
                                                style={{
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {product.name} ({product.code})
                                              </div>
                                              <div style={{ marginRight: "20px", color: "#666" }}>
                                                {t("color")}: {product.color || "-"}
                                              </div>
                                              <div
                                                style={{
                                                  color:
                                                    product.stock === 0
                                                      ? "#f5222d"
                                                      : product.stock < 30
                                                        ? "#faad14"
                                                        : "#52c41a",
                                                }}
                                              >
                                                {t("stock")}: {product.stock || 0}
                                              </div>
                                            </div>
                                          </Select.Option>
                                        ))}
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Item
                                    {...fieldProps}
                                    name={[field.name, "amount"]}
                                    label={t("order.amount")}
                                    rules={[{ required: true, message: t("form.enter_value") }]}
                                    style={{ marginBottom: 8 }}
                                  >
                                    <InputNumber min={1} placeholder="0" style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Item
                                    {...fieldProps}
                                    name={[field.name, "priority"]}
                                    valuePropName="checked"
                                    style={{ marginBottom: 8 }}
                                  >
                                    <Checkbox style={{ marginTop: 30 }}>{t("priority")}</Checkbox>
                                  </Form.Item>
                                </Col>
                              </Row>
                            )}

                            {/* Packaging & Price Row - Only for non-draft products */}
                            {!isDraftProduct && (
                              <Row gutter={[16, 8]}>
                                <Col xs={12} md={6}>
                                  <Form.Item
                                    {...fieldProps}
                                    name={[field.name, "innerPackagingQuantity"]}
                                    label={t("inner_packaging_qty")}
                                    tooltip={t("inner_packaging_qty_tooltip")}
                                    rules={[{ required: true, message: t("form.enter_value") }]}
                                    style={{ marginBottom: 8 }}
                                  >
                                    <InputNumber min={1} placeholder="0" style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                                <Col xs={12} md={6}>
                                  <Form.Item
                                    {...fieldProps}
                                    name={[field.name, "masterPackagingQuantity"]}
                                    label={t("master_packaging_qty")}
                                    tooltip={t("master_packaging_qty_tooltip")}
                                    rules={[{ required: true, message: t("form.enter_value") }]}
                                    style={{ marginBottom: 8 }}
                                  >
                                    <InputNumber min={1} placeholder="0" style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                  <Form.Item
                                    {...fieldProps}
                                    name={[field.name, "price"]}
                                    label={t("price_per_unit")}
                                    rules={[{ required: true, message: t("form.enter_value") }]}
                                    style={{ marginBottom: 8 }}
                                  >
                                    <InputNumber
                                      min={0}
                                      step={0.01}
                                      precision={2}
                                      placeholder="0.00"
                                      style={{ width: "100%" }}
                                      prefix={selectedSupplier?.currency || "$"}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            )}

                            {/* Checkbox for attachments */}
                            {currentProductId && (
                              <Row gutter={16} style={{ marginTop: 8 }}>
                                <Col xs={24}>
                                  <Checkbox
                                    checked={hasAttachments}
                                    onChange={(e) => handleProductAttachmentToggle(currentProductId, e.target.checked)}
                                  >
                                    Voeg afbeelding en/of opmerking toe aan dit product
                                  </Checkbox>
                                </Col>
                              </Row>
                            )}

                            {/* Show upload and comment fields when checkbox is checked */}
                            {currentProductId && hasAttachments && (
                              <div style={{ marginTop: 12, padding: 12, background: "#fafafa", borderRadius: 4 }}>
                                <Row gutter={16}>
                                  <Col xs={24}>
                                    <div style={{ marginBottom: 8 }}>
                                      <label style={{ fontWeight: 500, marginBottom: 4, display: "block" }}>
                                        {t("images")}
                                      </label>
                                      <Upload
                                        listType="picture"
                                        fileList={productAttachments[currentProductId]?.fileList || []}
                                        onChange={(info) => handleProductImagesChange(currentProductId, info)}
                                        beforeUpload={() => false}
                                        multiple
                                      >
                                        <Button icon={<UploadOutlined />} size="small">
                                          {t("upload_images")}
                                        </Button>
                                      </Upload>
                                    </div>
                                  </Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col xs={24}>
                                    <div>
                                      <label style={{ fontWeight: 500, marginBottom: 4, display: "block" }}>
                                        {t("comments")}
                                      </label>
                                      <Input.TextArea
                                        rows={2}
                                        placeholder={t("add_comments")}
                                        value={productAttachments[currentProductId]?.comment || ""}
                                        onChange={(e) => handleProductCommentChange(currentProductId, e.target.value)}
                                      />
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </Collapse.Panel>
                        );
                      })}
                    </Collapse>
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        {t("add_product")}
                      </Button>
                    </Form.Item>
                  </>
                );
              }}
            </Form.List>

            <Form.Item name="comments" label={t("comments")}>
              <Input.TextArea rows={4} placeholder={t("add_comments")} />
            </Form.Item>

            <Form.Item label={t("images")}>
              <Upload
                listType="picture"
                fileList={fileList}
                onChange={async ({ fileList: newFileList }) => {
                  // Compress new files that don't have originFileObj compressed yet
                  const processedFileList = await Promise.all(
                    newFileList.map(async (file) => {
                      if (file.originFileObj && !file.compressed) {
                        const compressed = await compressImage(
                          file.originFileObj,
                          0.7, // quality (0.6-0.8 recommended)
                          1200, // max width
                        );
                        return {
                          ...file,
                          originFileObj: compressed,
                          compressed: true,
                        };
                      }
                      return file;
                    }),
                  );
                  setFileList(processedFileList);
                }}
                beforeUpload={() => false}
                multiple
              >
                <Button icon={<UploadOutlined />}>{t("upload_images")}</Button>
              </Upload>
            </Form.Item>

            {/* Order Summary */}
            <Form.Item noStyle shouldUpdate>
              {() => {
                const formProducts = addForm.getFieldValue("products") || [];
                const exchangeRate = addForm.getFieldValue("exchangeRate") || 0;
                const selectedSupplierId = addForm.getFieldValue("supplier");
                const selectedSupplier = suppliers.find((s) => s.id === selectedSupplierId);

                const totalItems = formProducts.reduce((sum, p) => sum + (p?.amount || 0), 0);
                const totalPriceUsd = formProducts.reduce((sum, p) => sum + (p?.amount || 0) * (p?.price || 0), 0);
                const totalPriceEur = totalPriceUsd * exchangeRate;

                return (
                  <div
                    style={{
                      background: "#f6f8fa",
                      padding: "16px",
                      borderRadius: 8,
                      marginTop: 16,
                      border: "1px solid #e8e8e8",
                    }}
                  >
                    <h4 style={{ margin: "0 0 12px 0" }}>{t("summary")}</h4>
                    <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
                      <div>
                        <span style={{ color: "#666", fontSize: 12 }}>{t("products")}</span>
                        <div style={{ fontWeight: 600, fontSize: 18 }}>{formProducts.length}</div>
                      </div>
                      <div>
                        <span style={{ color: "#666", fontSize: 12 }}>{t("total_items")}</span>
                        <div style={{ fontWeight: 600, fontSize: 18 }}>{totalItems.toLocaleString()}</div>
                      </div>
                      <div>
                        <span style={{ color: "#666", fontSize: 12 }}>{t("total_price")}</span>
                        <div style={{ fontWeight: 600, fontSize: 18, color: "#1890ff" }}>
                          {formatPriceByCurrency(totalPriceUsd, selectedSupplier?.currency)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Add Draft Product Modal */}
      <Modal
        title={t("product_draft.add_draft_product")}
        open={addDraftProductModalOpen}
        onCancel={() => {
          setAddDraftProductModalOpen(false);
          draftProductForm.resetFields();
        }}
        onOk={handleAddDraftProduct}
        okText={t("add")}
      >
        <Form form={draftProductForm} layout="vertical">
          <Form.Item
            name="productName"
            label={t("product_draft.draft_product_name")}
            rules={[{ required: true, message: t("form.enter_value") }]}
          >
            <Input placeholder="Bijv. Amazez Large" />
          </Form.Item>
          <Form.Item
            name="productCode"
            label={t("product_draft.draft_product_code")}
            rules={[{ required: true, message: t("form.enter_value") }]}
          >
            <Input placeholder="Bijv. BG10000" />
          </Form.Item>
          <Form.Item name="productBarcode" label={t("product_draft.draft_product_barcode")}>
            <Input placeholder="Bijv. 8712345678901" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const MobilePurchaseOrderCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 4px;

  .ant-card-body {
    padding: 12px;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default PurchaseOrdersOverview;
